// src/web/cameraStream.js
//
// Wraps getUserMedia to provide the front-facing camera stream to a <video>
// element. Negotiates a sensible resolution: 720p preferred, falls back to
// whatever the device offers.
//
// Returns a Promise that resolves once the video has loaded the first frame
// (readyState >= 2), so the caller knows the stream is paintable.

export async function attachCameraStream(videoEl) {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    throw new Error('getUserMedia is not supported in this browser');
  }

  const constraints = {
    audio: false,
    video: {
      facingMode: 'user',
      // Prefer 1080p for capable webcams (e.g. Logitech Brio); the browser
      // falls back to the best the camera supports. Frame-locking renders the
      // feed through the canvas, so native resolution matters for sharpness.
      width:  { ideal: 1920 },
      height: { ideal: 1080 },
      frameRate: { ideal: 30, max: 60 }
    }
  };

  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia(constraints);
  } catch (err) {
    // The most common reasons: user denied, no camera, busy elsewhere.
    const reason = err.name === 'NotAllowedError'
      ? 'permission-denied'
      : err.name === 'NotFoundError'
        ? 'no-camera'
        : 'camera-error';
    const e = new Error(reason);
    e.cause = err;
    throw e;
  }

  videoEl.srcObject = stream;
  videoEl.setAttribute('playsinline', 'true');

  await new Promise((resolve, reject) => {
    const onLoaded = () => { videoEl.removeEventListener('loadeddata', onLoaded); resolve(); };
    videoEl.addEventListener('loadeddata', onLoaded);
    setTimeout(() => reject(new Error('video-load-timeout')), 8000);
  });

  await videoEl.play().catch(() => { /* autoplay policy: ignore */ });

  return stream;
}

export function detachCameraStream(videoEl) {
  const stream = videoEl.srcObject;
  if (stream) {
    stream.getTracks().forEach(t => t.stop());
    videoEl.srcObject = null;
  }
}
